import { ChevronDown, Menu, Search, ClipboardList, Settings, Sparkles, LogIn, LogOut, Shield, User } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
} from "@/components/ui/sheet";
import { Link, useNavigate } from "react-router-dom";
import bidragsassistentenLogo from "@/assets/bidragsassistenten-logo-2.png";
import { useAuth } from "@/hooks/use-auth";
import { useAdmin } from "@/hooks/use-admin";

const navItems = [
  { label: "AI Sök", href: "/search", icon: Sparkles },
  { label: "Stiftelsedatabas", href: "/stiftelsedatabas", icon: Search },
  { label: "Skriv & hantera ansökningar", href: "/manage", icon: ClipboardList },
  { label: "Organisationsinställningar", href: "/installningar", icon: Settings },
];

const Navigation = () => {
  const { user, isAuthenticated, signOut, loading } = useAuth();
  const { isAdmin } = useAdmin();
  const navigate = useNavigate();

  const handleSignOut = async () => {
    await signOut();
    navigate("/");
  };

  const getUserInitials = () => {
    if (!user?.email) return "?";
    return user.email.substring(0, 2).toUpperCase();
  };

  const getUserDisplayName = () => {
    if (!user?.email) return "Gäst";
    return user.email.split("@")[0];
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 h-16 flex items-center justify-between px-6 md:px-8 shadow-lg relative overflow-hidden" style={{ background: 'linear-gradient(to right, rgba(107, 122, 161, 0.85) 0%, rgba(216, 124, 106, 0.85) 100%)' }}>
      
      {/* Logo */}
      <Link to="/search" className="flex-shrink-0">
        <img 
          src={bidragsassistentenLogo} 
          alt="Bidragsassistenten" 
          className="h-7 w-auto cursor-pointer hover:opacity-90 transition-opacity invert"
        />
      </Link>

      {/* Desktop Navigation */}
      <div className="hidden md:flex items-center gap-2">
        {navItems.map((item) => (
          <Button
            key={item.label}
            variant="ghost"
            asChild
            className="
              text-white/90 hover:text-white hover:bg-white/15
              text-sm font-semibold h-10 px-4 rounded-xl 
              transition-all duration-200
              gap-2.5
            "
          >
            <Link to={item.href}>
              <div className="h-8 w-8 rounded-lg bg-white/20 flex items-center justify-center">
                <item.icon className="h-4 w-4 text-white" />
              </div>
              {item.label}
            </Link>
          </Button>
        ))}
      </div>

      {/* User Dropdown - Desktop */}
      <div className="hidden md:block">
        {loading ? (
          <div className="h-8 w-8 rounded-full bg-white/25 animate-pulse" />
        ) : isAuthenticated ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                className="text-white/90 hover:text-white hover:bg-white/15 gap-2 h-10 px-3 rounded-xl transition-all duration-200"
              >
                <div className="h-8 w-8 rounded-full bg-white/25 flex items-center justify-center text-xs font-semibold text-white">
                  {getUserInitials()}
                </div>
                <span className="text-sm font-semibold">{getUserDisplayName()}</span>
                <ChevronDown className="h-4 w-4 opacity-70" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56 rounded-xl p-1.5">
              <div className="px-2 py-1.5 text-sm font-medium text-muted-foreground">
                {user?.email}
              </div>
              <DropdownMenuSeparator />
              <DropdownMenuItem asChild className="rounded-lg text-sm font-medium cursor-pointer">
                <Link to="/konto">
                  <User className="h-4 w-4 mr-2" />
                  Kontoinställningar
                </Link>
              </DropdownMenuItem>
              {isAdmin && (
                <DropdownMenuItem asChild className="rounded-lg text-sm font-medium cursor-pointer">
                  <Link to="/admin">
                    <Shield className="h-4 w-4 mr-2" />
                    Admin
                  </Link>
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem 
                onClick={handleSignOut}
                className="rounded-lg text-sm text-destructive cursor-pointer"
              >
                <LogOut className="h-4 w-4 mr-2" />
                Logga ut
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        ) : (
          <Button
            variant="ghost"
            asChild
            className="text-white/90 hover:text-white hover:bg-white/15 gap-2 h-10 px-4 rounded-xl transition-all duration-200"
          >
            <Link to="/auth">
              <LogIn className="h-4 w-4" />
              <span className="text-sm font-semibold">Logga in</span>
            </Link>
          </Button>
        )}
      </div>

      {/* Mobile Menu */}
      <div className="md:hidden">
        <Sheet>
          <SheetTrigger asChild>
            <Button variant="ghost" size="icon" className="text-white hover:bg-white/15 h-10 w-10 rounded-xl">
              <Menu className="h-5 w-5" />
            </Button>
          </SheetTrigger>
          <SheetContent side="right" className="w-80 p-6">
            <div className="flex flex-col gap-6 mt-6">
              {isAuthenticated ? (
                <div className="flex items-center gap-3 pb-5 border-b border-border/40">
                  <div className="h-12 w-12 rounded-full bg-gradient-to-br from-primary to-accent flex items-center justify-center text-base font-semibold text-white">
                    {getUserInitials()}
                  </div>
                  <div>
                    <p className="font-semibold text-foreground">{getUserDisplayName()}</p>
                    <p className="text-sm text-muted-foreground">{user?.email}</p>
                  </div>
                </div>
              ) : (
                <div className="pb-5 border-b border-border/40">
                  <Button asChild className="w-full">
                    <Link to="/auth">
                      <LogIn className="h-4 w-4 mr-2" />
                      Logga in
                    </Link>
                  </Button>
                </div>
              )}
              <div className="space-y-2">
                {navItems.map((item) => (
                  <Button
                    key={item.label}
                    variant="ghost"
                    asChild
                    className="justify-start text-foreground w-full h-12 rounded-xl gap-3 font-semibold"
                  >
                    <Link to={item.href}>
                      <div className="h-9 w-9 rounded-lg bg-accent/10 flex items-center justify-center">
                        <item.icon className="h-4 w-4 text-accent" />
                      </div>
                      {item.label}
                    </Link>
                  </Button>
                ))}
                {isAdmin && (
                  <Button
                    variant="ghost"
                    asChild
                    className="justify-start text-foreground w-full h-12 rounded-xl gap-3 font-semibold"
                  >
                    <Link to="/admin">
                      <div className="h-9 w-9 rounded-lg bg-accent/10 flex items-center justify-center">
                        <Shield className="h-4 w-4 text-accent" />
                      </div>
                      Admin
                    </Link>
                  </Button>
                )}
              </div>
              {isAuthenticated && (
                <div className="border-t border-border/40 pt-5 space-y-1">
                  <Button 
                    variant="ghost" 
                    onClick={handleSignOut}
                    className="justify-start text-destructive w-full h-10 rounded-xl"
                  >
                    <LogOut className="h-4 w-4 mr-2" />
                    Logga ut
                  </Button>
                </div>
              )}
            </div>
          </SheetContent>
        </Sheet>
      </div>
    </nav>
  );
};

export default Navigation;